"use client";

import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { ThemeProviderProps } from "next-themes/dist/types";
import { Provider as BalancerProvider } from "react-wrap-balancer";

import {
  applyAppearance,
  readAppearance,
  subscribeAppearance,
} from "@/lib/appearance";
import { SessionProvider } from "@/lib/auth-client";
import { isBrandHost } from "@/lib/share-links";
import { AccountPulseProvider } from "@/hooks/use-account-pulse";
import { DPoPAuthProvider } from "@/components/dpop-auth-context";

type WalletProvider = ComponentType<{ children: ReactNode }>;

const API_BASE =
  process.env.NEXT_PUBLIC_API_SERVER_URL ?? "https://api.fexr.io";
const APP_URL = process.env.NEXT_PUBLIC_APP_URL ?? "https://app.fexr.io";

type HostBrand = { display_name?: string | null; logo_url?: string | null };

// On a Fexr Desk workspace domain: the workspace's name and logo above the
// page, and its name in the tab title. Brand colours are not applied: they
// can't be held to the presets' contrast floors.
function HostBrandBar({ pathname }: { pathname: string | null }) {
  const [brand, setBrand] = useState<HostBrand | null>(null);
  useEffect(() => {
    if (!isBrandHost(window.location.host)) return;
    let active = true;
    fetch(
      `${API_BASE}/v1/branding/by-host?host=${encodeURIComponent(window.location.hostname)}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (active && b) setBrand(b);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const name = brand?.display_name;
    if (name && !document.title.startsWith(`${name} · `))
      document.title = `${name} · ${document.title}`;
  }, [brand, pathname]);
  if (!brand?.display_name && !brand?.logo_url) return null;
  return (
    <div className="glass-bar sticky top-0 z-40 flex h-8 items-center gap-2 border-b border-border/60 px-4 text-xs">
      {brand.logo_url && (
        // eslint-disable-next-line @next/next/no-img-element -- workspace logo on an allowlisted https URL
        <img
          src={brand.logo_url}
          alt=""
          className="h-4 w-auto max-w-24 object-contain"
        />
      )}
      {brand.display_name && (
        <span className="truncate text-foreground">{brand.display_name}</span>
      )}
      <a
        href={APP_URL}
        className="ml-auto shrink-0 text-muted-foreground transition-colors duration-1 hover:text-foreground"
      >
        On Fexr
      </a>
    </div>
  );
}

// Only routes whose complete component tree is wallet-independent skip wallet
// providers. The public home requests auth by navigating to /login; analysis,
// response, QR, and dashboard trees continue to keep the full wallet context.
const WALLET_FREE_ROUTES = [
  "/",
  "/waitlist",
  "/install",
  "/share",
  "/~offline",
];

// Public, crawlable pages (and everything under them). Behind the wallet stack
// their server HTML was only the spinner below, so crawlers that don't run JS
// (most AI answer engines) saw an empty page. None of them use a wallet hook.
const WALLET_FREE_PREFIXES = [
  "/markets",
  "/w",
  "/strategies",
  "/status",
  "/analysis",
  "/vaults",
];

function isWalletFreeRoute(pathname: string) {
  return (
    WALLET_FREE_ROUTES.includes(pathname) ||
    WALLET_FREE_PREFIXES.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    )
  );
}

// Auto-recover from stale build chunks. After a new deploy, a client still
// running the previous HTML requests chunk hashes that no longer exist; the CDN
// answers those `/_next/static/*` requests with its 404 HTML, which the browser
// refuses to execute ("MIME type ('text/html') is not executable" / ChunkLoadError).
// A single hard reload pulls fresh HTML with valid hashes. Guarded so a genuinely
// broken deploy can't trigger a reload loop.
function useChunkErrorRecovery() {
  useEffect(() => {
    const RELOAD_KEY = "chunk-reload-at";
    const RELOAD_WINDOW_MS = 10_000;

    const reloadOnce = () => {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last < RELOAD_WINDOW_MS) return; // already tried recently
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
    };

    // Failed <script>/<link> for a Next static chunk (capture phase — resource
    // load errors don't bubble).
    const onResourceError = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const url =
        (target as HTMLScriptElement).src ||
        (target as HTMLLinkElement).href ||
        "";
      if (url.includes("/_next/static/")) reloadOnce();
    };

    // Dynamic import() rejections surface as ChunkLoadError.
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const name = reason?.name || "";
      const message = String(reason?.message || reason || "");
      if (
        name === "ChunkLoadError" ||
        /Loading (?:CSS )?chunk .* failed/.test(message)
      ) {
        reloadOnce();
      }
    };

    window.addEventListener("error", onResourceError, true);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onResourceError, true);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
}

// The boot script paints the saved theme before first paint, but a hydration
// error outside a Suspense boundary makes React re-render the whole document,
// <html> included, which drops data-theme. Re-apply after hydration, on every
// route change, and when another tab changes it.
function useAppearanceSync(pathname: string | null) {
  useEffect(() => {
    applyAppearance(readAppearance());
  }, [pathname]);
  useEffect(
    () => subscribeAppearance(() => applyAppearance(readAppearance())),
    [],
  );
}

export function Providers({ children, ...props }: ThemeProviderProps) {
  const pathname = usePathname();
  useChunkErrorRecovery();
  useAppearanceSync(pathname);
  const walletFree = isWalletFreeRoute(pathname || "/");
  const [WalletProviderStack, setWalletProviderStack] =
    useState<WalletProvider | null>(null);

  // A native, runtime import is deliberate here. next/dynamic declarations in
  // this shared root module advertise their Web3 chunks to every route, even
  // when the wallet-free branch never renders them. Loading after the pathname
  // check keeps wagmi, viem, noble and RainbowKit off the marketing critical
  // path while preserving the complete provider tree everywhere else.
  useEffect(() => {
    if (walletFree || WalletProviderStack) return;
    let active = true;
    import("@/components/wallet-provider-stack").then((mod) => {
      if (active) setWalletProviderStack(() => mod.WalletProviderStack);
    });
    return () => {
      active = false;
    };
  }, [walletFree, WalletProviderStack]);

  if (walletFree) {
    // Minimal providers only for explicitly wallet-free routes
    return (
      <NextThemesProvider {...props}>
        <BalancerProvider>
          <SessionProvider>
            <DPoPAuthProvider>
              <AccountPulseProvider>
                <HostBrandBar pathname={pathname} />
                {children}
              </AccountPulseProvider>
            </DPoPAuthProvider>
          </SessionProvider>
        </BalancerProvider>
      </NextThemesProvider>
    );
  }

  if (!WalletProviderStack) {
    return (
      <div
        className="flex min-h-dvh items-center justify-center bg-background"
        role="status"
        aria-label="Initializing secure wallet"
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <NextThemesProvider {...props}>
      <BalancerProvider>
        <SessionProvider>
          <DPoPAuthProvider>
            <AccountPulseProvider>
              <WalletProviderStack>
                <HostBrandBar pathname={pathname} />
                {children}
              </WalletProviderStack>
            </AccountPulseProvider>
          </DPoPAuthProvider>
        </SessionProvider>
      </BalancerProvider>
    </NextThemesProvider>
  );
}
